// src/components/Common/utils/millRate.js

/**
 * Mill Rate Utilities
 * ---------------------
 * This module provides:
 * 1. `useMillRate`: A custom React hook to fetch the latest mill rate
 *    from the CT open data API for a given town.
 * 2. `calculateEstimatedTaxes`: A utility function to estimate property taxes
 *    using the assessed value and the town's mill rate.
 *
 * Dependencies:
 * - React useState, useEffect
 * - COLLECTION_MAP: a normalized mapping of towns to municipality names
 */

import { useState, useEffect } from "react";
import { COLLECTION_MAP } from "../../../shared/constants";

// -----------------------------------------------------------------------------
// useMillRate(town)
// -----------------------------------------------------------------------------
/**
 * Custom React hook to fetch the latest real/personal property mill rate
 * for a given Connecticut town using the CT Open Data API.
 *
 * @param {string} town - User-input town name (e.g., "Glastonbury")
 * @returns {Object} { millRate, loading, error }
 *
 * The town is normalized and mapped using COLLECTION_MAP before making the API call.
 * Data is fetched from: https://
 */
export const useMillRate = (town) => {
  const [millRate, setMillRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!town) {
      console.warn("🛑 No town provided to useMillRate");
      setLoading(false);
      return;
    }

    const normalizedTownKey = town.toLowerCase().replace(/[\s,().'-]/g, "");
    const mappedTown = COLLECTION_MAP[normalizedTownKey];

    if (!mappedTown) {
      console.warn(`❓ Town "${town}" could not be mapped from key "${normalizedTownKey}"`);
      setError("Invalid or unsupported town");
      setMillRate(null);
      setLoading(false);
      return;
    }

    const fetchMillRate = async () => {
      setLoading(true);
      setError(null);

      try {
        const encodedTown = encodeURIComponent(mappedTown);
        
        const response = await fetch(
         `https://data.ct.gov/resource/emyx-j53e.json?municipality=${encodedTown}&$order=fiscal_year DESC&$limit=1`
        );

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (data?.length > 0 && data[0].mill_rate_real_personal) {
          setMillRate(parseFloat(data[0].mill_rate_real_personal));
        } else {
          setError("Mill rate data not available");
          setMillRate(null);
        }
      } catch (err) {
        console.error("❌ Mill rate fetch error:", err);
        setError(err.message);
        setMillRate(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMillRate();
  }, [town]);

  return { millRate, loading, error };
};

// -----------------------------------------------------------------------------
// calculateEstimatedTaxes(assessedValue, millRate)
// -----------------------------------------------------------------------------
/**
 * Estimate annual property taxes based on the assessed value and mill rate.
 *
 * @param {number|string} assessedValue - Assessed value of the property (e.g., 250000)
 * @param {number|null} millRate - Mill rate (e.g., 35.2)
 * @returns {string} Estimated tax formatted as USD (e.g., "$8,800") or "N/A" if invalid
 *
 * Formula: (assessedValue / 1000) * millRate
 */
export const calculateEstimatedTaxes = (assessedValue, millRate) => {
  if (assessedValue == null || millRate == null) return "N/A";

  const cleaned = typeof assessedValue === "string" 
    ? assessedValue.replace(/[^0-9.-]+/g, "")
    : String(assessedValue);
  
  if (!cleaned) return "N/A";

  const numeric = Number(cleaned);
  if (isNaN(numeric)) return "N/A";

  const taxAmount = Math.round((numeric / 1000) * millRate);
  return `$${taxAmount.toLocaleString("en-US")}`;
};